import React, { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { BuildingWithConvertedAddresses } from '../../utils/types';
import { footprintFromRenderedMap } from '../../HouseMap/footprintFromRenderedMap';
import './BuildingHeroMap.scss';

/**
 * A drawable outer ring — at least 3 vertices. Rejects stale `coordinates: []`
 * / `[[]]`, AND the degenerate single-vertex ring (`[[[lng,lat]]]`) some listing
 * cards synthesise from an address point, which would otherwise pass and paint
 * an invalid one-point polygon. Matches the backend `_has_usable_ring`.
 */
function hasUsableRing(coords: unknown): coords is number[][][] {
  return (
    Array.isArray(coords) &&
    coords.length > 0 &&
    Array.isArray(coords[0]) &&
    (coords[0] as unknown[]).length >= 3
  );
}

/**
 * A footprint the static overlay can draw, with its GeoJSON type preserved so
 * Polygon and MultiPolygon both render correctly.
 */
type Footprint = {
  type: 'Polygon' | 'MultiPolygon';
  coordinates: number[][][] | number[][][][];
};

/**
 * Return the geometry only if it carries a drawable outer ring (Polygon, or any
 * member of a MultiPolygon). Otherwise null — the signal to resolve by id.
 */
function footprintIfDrawable(geom: unknown): Footprint | null {
  const g = geom as { type?: string; coordinates?: unknown } | null;
  if (!g || !Array.isArray(g.coordinates)) return null;
  if (g.type === 'Polygon' && hasUsableRing(g.coordinates)) return g as Footprint;
  if (
    g.type === 'MultiPolygon' &&
    (g.coordinates as unknown[]).some((poly) => hasUsableRing(poly))
  ) {
    return g as Footprint;
  }
  return null;
}

const TRUSTED_SOURCES = new Set(['mapbox-union-v2']);
/**
 * The hero trusts geometry tagged 'mapbox-union-v2' directly: it is a backend
 * Mapbox-union sourced at a fixed z16, so it is COMPLETE and zoom-independent and
 * draws without any further resolution. The on-map capture ('mapbox-tiles') comes
 * from queryRenderedFeatures and shrinks at low zoom, so the hero re-resolves it
 * from its OWN rendered `building` layer (queried at display zoom >= 16) to draw
 * the whole footprint. Legacy `building-data` geometry is untagged → not drawn.
 */
function trustedFootprint(
  geom: unknown,
  source: string | undefined,
): Footprint | null {
  if (!source || !TRUSTED_SOURCES.has(source)) return null;
  return footprintIfDrawable(geom);
}

/**
 * Footprints resolved by id from the hero's OWN rendered `building` layer
 * (queryRenderedFeatures unioned to dissolve tile seams). Keyed by id, so every
 * open path that arrives without a drawable ring — listing icon, deep link,
 * feed/search card, low-zoom click — still paints the real outline, and we
 * resolve each building at most once per session. `null` = resolution failed.
 */
const resolvedGeometryCache = new Map<string, Footprint | null>();

interface BuildingHeroMapProps {
  selectedBuilding: BuildingWithConvertedAddresses;
  /**
   * Called exactly once per building resolution with the final footprint (the
   * trusted own footprint, the rendered-map-resolved footprint, or null) so the
   * parent can share it (e.g. with side-address frontage detection).
   */
  onFootprintResolved?: (
    geometry: GeoJSON.Polygon | GeoJSON.MultiPolygon | null,
  ) => void;
}

/**
 * Exact values sampled from the Figma hero close-up.
 */
const OUTLINE = '#1EA37C';
const OUTLINE_WIDTH = 2;
const FILL = '#ffffff';

/**
 * Figma hero look: mute the WHOLE basemap to greys so only the highlighted
 * footprint reads in colour. A monochrome palette — land/water/buildings/roads
 * in a tight grey range, labels softened. Applied per map instance (not the
 * main map). Our `hero-*` layers are added afterwards and keep their colour.
 */
const HERO_GREY = {
  land: '#e8eaed',
  water: '#dbe0e5',
  building: '#d3d8de',
  edge: '#c6cbd2',
  road: '#f4f5f7',
  line: '#dfe3e7',
  text: '#9aa1a9',
};

/**
 * Recolour every base-style layer to greys, skipping our footprint layers.
 */
function greyBasemap(map: mapboxgl.Map): void {
  const layers = map.getStyle().layers || [];
  for (const layer of layers) {
    const id = layer.id;
    if (id === 'hero-fill' || id === 'hero-outline') continue;
    const lid = id.toLowerCase();
    const paint = (layer as { paint?: Record<string, unknown> }).paint;
    try {
      switch (layer.type) {
        case 'background':
          map.setPaintProperty(id, 'background-color', HERO_GREY.land);
          break;
        case 'fill':
          map.setPaintProperty(
            id,
            'fill-color',
            lid.includes('water')
              ? HERO_GREY.water
              : lid.includes('building')
                ? HERO_GREY.building
                : HERO_GREY.land,
          );
          if (paint && 'fill-outline-color' in paint) {
            map.setPaintProperty(id, 'fill-outline-color', HERO_GREY.edge);
          }
          break;
        case 'line':
          map.setPaintProperty(
            id,
            'line-color',
            /road|street|bridge|tunnel|motorway|trunk|primary|secondary|tertiary|path|rail/.test(lid)
              ? HERO_GREY.road
              : HERO_GREY.line,
          );
          break;
        case 'fill-extrusion':
          map.setPaintProperty(id, 'fill-extrusion-color', HERO_GREY.building);
          break;
        case 'symbol':
          try {
            map.setPaintProperty(id, 'text-color', HERO_GREY.text);
            map.setPaintProperty(id, 'text-halo-color', '#ffffff');
          } catch (e) {
            void e;
          }
          break;
        default:
          break;
      }
    } catch (e) {
      void e;
    }
  }
}

const isFiniteNum = (n: unknown): n is number => typeof n === 'number' && Number.isFinite(n);

/**
 * Resolve a {lat,lng} to centre the static map on. Buildings saved before their
 * geometry was enriched come back with centroid: {} (empty-but-truthy) — so
 * check the VALUES are finite, not that the object exists. Falls back to the
 * first address's coordinates.
 */
function resolveCenter(
  building: BuildingWithConvertedAddresses,
): { lat: number; lng: number } | null {
  const c = building.centroid;
  if (c && isFiniteNum(c.lng) && isFiniteNum(c.lat)) return { lat: c.lat, lng: c.lng };
  const a = building.addresses?.[0]?.coordinates;
  if (a && isFiniteNum(a.lng) && isFiniteNum(a.lat)) return { lat: a.lat, lng: a.lng };
  return null;
}

/**
 * Collect every [lng,lat] vertex across a Polygon (number[][][]) or
 * MultiPolygon (number[][][][]) so we can fit-bounds the whole footprint.
 */
function collectCoords(footprint: Footprint): number[][] {
  const out: number[][] = [];
  const walk = (node: unknown): void => {
    if (!Array.isArray(node)) return;
    if (node.length >= 2 && isFiniteNum(node[0]) && isFiniteNum(node[1])) {
      out.push(node as number[]);
      return;
    }
    for (const child of node) walk(child);
  };
  walk(footprint.coordinates);
  return out;
}

/**
 * Building-view hero map (Figma 2172:12447 header visual).
 *
 * Non-interactive → a client-side Mapbox GL map (`interactive: false`) on the
 * jaakkocom custom style. The footprint is highlighted (white fill + #1EA37C
 * outline) and `fitBounds` with padding frames it with street context; a black
 * centre dot marks it. When no footprint is known, the map jumps to the centre
 * at building-scale zoom. Drawing on a GL canvas avoids the slow Static-Images
 * server render.
 *
 * RESPONSIVE: a ResizeObserver measures the container; the GL map is created
 * only once the container has a real (non-zero) size, so a `display:none` twin
 * hero (0×0) never spins up a second WebGL context.
 */
const BuildingHeroMap: React.FC<BuildingHeroMapProps> = ({
  selectedBuilding,
  onFootprintResolved,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const [mapReady, setMapReady] = useState(false);
  const [size, setSize] = useState<{ w: number; h: number } | null>(null);
  // Bumped when the style becomes ready after the resolve effect found it
  // still loading, so that effect re-runs instead of bailing forever (JAA-354:
  // mapReady flips right after greyBasemap's paint pass, so the style is often
  // momentarily dirty at that exact moment).
  const [styleReadyTick, setStyleReadyTick] = useState(0);

  const [resolved, setResolved] = useState<Footprint | null | undefined>(undefined);
  const ownFootprint = trustedFootprint(
    selectedBuilding.geometry,
    selectedBuilding.geometry_source,
  );

  /**
   * Always call the LATEST `onFootprintResolved` from resolution effects without
   * re-running them when the parent passes a new closure.
   */
  const onFootprintResolvedRef = useRef(onFootprintResolved);
  onFootprintResolvedRef.current = onFootprintResolved;

  const [resolvedForId, setResolvedForId] = useState(selectedBuilding.id);
  if (resolvedForId !== selectedBuilding.id) {
    setResolvedForId(selectedBuilding.id);
    setResolved(
      resolvedGeometryCache.has(selectedBuilding.id)
        ? resolvedGeometryCache.get(selectedBuilding.id)!
        : undefined,
    );
    setMapReady(false);
  }

  /**
   * Track the id whose footprint we've already reported to the parent so
   * `onFootprintResolved` fires exactly once per building resolution, despite
   * StrictMode double-invokes and re-renders.
   */
  const reportedForIdRef = useRef<string | null>(null);
  const reportFootprint = (
    id: string,
    geometry: Footprint | null,
  ): void => {
    if (reportedForIdRef.current === id) return;
    reportedForIdRef.current = id;
    onFootprintResolvedRef.current?.(
      geometry as GeoJSON.Polygon | GeoJSON.MultiPolygon | null,
    );
  };

  /**
   * Seed the resolution state for the current building. A trusted own footprint
   * draws directly (no query); otherwise a cached result is reused, or we leave
   * `resolved` undefined so the map's `idle` handler resolves it from the
   * rendered `building` layer. No /building-geometry fetch — the footprint comes
   * from the hero's OWN tiles (approach A).
   */
  useEffect(() => {
    if (ownFootprint) {
      setResolved(undefined);
      reportFootprint(selectedBuilding.id, ownFootprint);
      return;
    }
    const id = selectedBuilding.id;
    if (!id) {
      setResolved(null);
      return;
    }

    const cached = resolvedGeometryCache.get(id);
    if (cached !== undefined) {
      setResolved(cached);
      reportFootprint(id, cached);
    } else {
      setResolved(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBuilding.id, selectedBuilding.geometry, selectedBuilding.geometry_source]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    let timer: ReturnType<typeof setTimeout> | null = null;
    const measure = () => {
      const rect = el.getBoundingClientRect();
      const w = Math.round(rect.width);
      const h = Math.round(rect.height);
      if (w > 0 && h > 0) {
        setSize((prev) => (prev && prev.w === w && prev.h === h ? prev : { w, h }));
      }
    };
    measure();

    const ro = new ResizeObserver(() => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(measure, 200);
    });
    ro.observe(el);
    return () => {
      ro.disconnect();
      if (timer) clearTimeout(timer);
    };
  }, []);

  const footprint = ownFootprint ?? (resolved || null);

  const pending = !ownFootprint && resolved === undefined;

  /**
   * Draw (or redraw) the footprint on the live map and frame it. Operates on the
   * current map; called on style `'load'` and again whenever the building or
   * footprint changes, so switching buildings re-draws WITHOUT recreating the
   * map (no WebGL context churn). The map must be style-loaded before calling.
   */
  const drawFootprint = (map: mapboxgl.Map): void => {
    if (footprint) {
      const data: GeoJSON.Feature = {
        type: 'Feature',
        properties: {},
        geometry: footprint as GeoJSON.Geometry,
      };
      const existing = map.getSource('hero-footprint') as
        | mapboxgl.GeoJSONSource
        | undefined;
      if (existing) {
        existing.setData(data);
      } else {
        map.addSource('hero-footprint', { type: 'geojson', data });
        map.addLayer({
          id: 'hero-fill',
          type: 'fill',
          source: 'hero-footprint',
          paint: { 'fill-color': FILL, 'fill-opacity': 1 },
        });
        map.addLayer({
          id: 'hero-outline',
          type: 'line',
          source: 'hero-footprint',
          paint: { 'line-color': OUTLINE, 'line-width': OUTLINE_WIDTH },
          layout: { 'line-join': 'round', 'line-cap': 'round' },
        });
      }

      const coords = collectCoords(footprint);
      if (coords.length) {
        let minLng = Infinity;
        let minLat = Infinity;
        let maxLng = -Infinity;
        let maxLat = -Infinity;
        for (const [lng, lat] of coords) {
          if (lng < minLng) minLng = lng;
          if (lat < minLat) minLat = lat;
          if (lng > maxLng) maxLng = lng;
          if (lat > maxLat) maxLat = lat;
        }
        const padding = size
          ? Math.min(Math.max(Math.round(Math.min(size.w, size.h) * 0.16), 20), 80)
          : 40;
        map.fitBounds(
          [
            [minLng, minLat],
            [maxLng, maxLat],
          ],
          { padding, animate: false, duration: 0 },
        );
      }
      return;
    }

    const existing = map.getSource('hero-footprint') as
      | mapboxgl.GeoJSONSource
      | undefined;
    if (existing) {
      existing.setData({ type: 'FeatureCollection', features: [] });
    }
    const center = resolveCenter(selectedBuilding);
    if (center) {
      map.jumpTo({ center: [center.lng, center.lat], zoom: DISPLAY_ZOOM });
    }
  };

  /**
   * Always invoke the LATEST draw closure from the 'load' handler — the
   * footprint may resolve between map creation and the style 'load' firing, and
   * a captured `drawFootprint` would otherwise paint a stale frame.
   */
  const drawFootprintRef = useRef(drawFootprint);
  drawFootprintRef.current = drawFootprint;

  /**
   * Display zoom for the hero close-up. >= 16 so the `building` tiles render and
   * the approach-A union is the COMPLETE footprint when queried. Kept low enough
   * (16.5) that a large building fits the viewport before queryRenderedFeatures
   * runs — at 17.5 a big footprint came back clipped, so the outline drew cut.
   */
  const DISPLAY_ZOOM = 16.5;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!mapContainerRef.current) return;
    if (!size || size.w <= 0 || size.h <= 0) return;
    if (mapRef.current) return;

    const center = resolveCenter(selectedBuilding);

    mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN!;
    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: `mapbox://styles/jaakkocom/${process.env.REACT_APP_MAPBOX_STYLE_ID}`,
      interactive: false,
      attributionControl: false,
      dragRotate: false,
      // Frame the single building at display zoom >= 16 so its `building` tiles
      // render for the approach-A query; `drawFootprint`/`fitBounds` re-frames
      // once a footprint is known.
      ...(center ? { center: [center.lng, center.lat] as [number, number], zoom: DISPLAY_ZOOM } : {}),
    });
    mapRef.current = map;

    // Required basemap (Mapbox/OSM) attribution per Mapbox ToS — must stay
    // visible; compact, bottom-right.
    map.addControl(
      new mapboxgl.AttributionControl({
        compact: true,
      }),
      'bottom-right',
    );

    let disposed = false;
    let setupFallbackTimer: ReturnType<typeof setTimeout> | null = null;
    let drawTimer: ReturnType<typeof setTimeout> | null = null;
    let heroStyleApplied = false;

    /**
     * Mapbox can fire `load` before its first multi-frame symbol placement has
     * completely settled. Rewriting the base style and adding our overlay in
     * that same callback leaves a brief partially-hydrated symbol layer in the
     * placement list (`continuePlacement` then reads `layout.get` on
     * undefined). It is especially easy to hit while rapidly moving between a
     * building and its seller/buyer cards, because several map instances are
     * loading at once.
     *
     * Start at an idle boundary, then defer our addSource/addLayer work one
     * task beyond the base-paint updates. The timeout is only a fallback for a
     * static map that never reaches idle after load.
     */
    const applyHeroStyle = () => {
      if (
        disposed ||
        heroStyleApplied ||
        mapRef.current !== map ||
        !map.isStyleLoaded()
      ) {
        return;
      }
      heroStyleApplied = true;
      if (setupFallbackTimer) {
        clearTimeout(setupFallbackTimer);
        setupFallbackTimer = null;
      }

      greyBasemap(map);
      // greyBasemap's paint pass leaves the style momentarily dirty, so this
      // follow-up may find isStyleLoaded() false. Park on the next idle and
      // retry instead of returning: a one-shot bail here left mapReady false
      // forever and the skeleton covering a fully rendered map (JAA-354).
      const finishSetup = () => {
        map.off('idle', finishSetup);
        if (disposed || mapRef.current !== map) return;
        if (!map.isStyleLoaded()) {
          // Belt and braces like every other wait in this file: idle for the
          // normal case, a timeout for a static map that emits no later idle.
          map.once('idle', finishSetup);
          drawTimer = setTimeout(finishSetup, 400);
          return;
        }
        drawFootprintRef.current(map);
        setMapReady(true);
      };
      drawTimer = setTimeout(finishSetup, 0);
    };

    const handleLoad = () => {
      map.once('idle', applyHeroStyle);
      setupFallbackTimer = setTimeout(applyHeroStyle, 1000);
    };
    map.on('load', handleLoad);

    return () => {
      disposed = true;
      if (setupFallbackTimer) clearTimeout(setupFallbackTimer);
      if (drawTimer) clearTimeout(drawTimer);
      map.off('load', handleLoad);
      map.off('idle', applyHeroStyle);
      if (mapRef.current === map) {
        map.remove();
        mapRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [size]);

  /**
   * Resolve an uncached footprint for the CURRENT building from this map's own
   * rendered `building` layer. This is building-keyed rather than owned by the
   * map-creation effect: the map instance is deliberately reused, and a
   * building can change while its first load/idle callback is still deferred.
   *
   * Re-centre before waiting for idle so the current building's z16 tiles are
   * the ones queried. Cleanup cancels both paths when the building changes; the
   * timeout covers a static (`interactive:false`) map that emits no later idle.
   * A style still loading when this runs (mapReady flips right after the
   * greyBasemap paint pass) parks the effect on the next idle and re-runs it —
   * bailing here permanently was JAA-354's stuck-skeleton regression.
   */
  useEffect(() => {
    const map = mapRef.current;
    const buildingId = selectedBuilding.id;
    if (!mapReady || !pending || !buildingId || !map) {
      return;
    }
    if (!map.isStyleLoaded()) {
      // A momentarily-dirty style must delay resolution, never cancel it: wait
      // for the map's next idle (which follows every style settle) and re-run.
      // The timeout backstops a map that emits no later idle AND a map torn
      // down by a resize before its idle fires - the re-run reads the fresh
      // mapRef either way.
      const kick = () => setStyleReadyTick((tick) => tick + 1);
      map.once('idle', kick);
      const kickTimer = setTimeout(kick, 400);
      return () => {
        map.off('idle', kick);
        clearTimeout(kickTimer);
      };
    }

    let cancelled = false;
    const resolveFootprint = () => {
      if (cancelled || mapRef.current !== map) return;

      const cached = resolvedGeometryCache.get(buildingId);
      if (cached !== undefined) {
        setResolved(cached);
        reportFootprint(buildingId, cached);
        return;
      }

      const numericId = Number(buildingId);
      const geometry = footprintFromRenderedMap(
        map,
        Number.isFinite(numericId) ? numericId : buildingId,
      ) as Footprint | null;
      if (cancelled) return;
      resolvedGeometryCache.set(buildingId, geometry);
      setResolved(geometry);
      reportFootprint(buildingId, geometry);
    };

    // With `pending`, the latest draw closure has no footprint: it clears the
    // previous building's overlay and jumps to this building's centre.
    drawFootprintRef.current(map);
    map.once('idle', resolveFootprint);
    const fallbackTimer = setTimeout(resolveFootprint, 800);

    return () => {
      cancelled = true;
      clearTimeout(fallbackTimer);
      map.off('idle', resolveFootprint);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBuilding.id, pending, mapReady, styleReadyTick]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const map = mapRef.current;
    if (!map || pending) return;
    if (!map.isStyleLoaded()) return;
    drawFootprint(map);
    setMapReady(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [footprint, selectedBuilding.id, pending]);

  const showSkeleton = pending || !mapReady;

  return (
    <div className="building-hero-map" ref={containerRef}>
      <div className="building-hero-map__gl" ref={mapContainerRef} />
      {showSkeleton && (
        <div className="building-hero-map__skeleton" aria-hidden="true" />
      )}
      {!showSkeleton && <span className="building-hero-map__dot" aria-hidden="true" />}
    </div>
  );
};

export default BuildingHeroMap;
